// src/components/LayerPropertiesEditor.jsx
"use client";

import React, { useState, useEffect, useRef } from "react";
import { Save, Tag, Type } from "lucide-react";
import ColorBreakEditor from "./ColorBreakEditor";
import DotDensityEditor from "./DotDensityEditor";
import PointStyleEditor from "./PointStyleEditor"; // For 'comp' and 'pipeline'
import CustomPointStyleEditor from "./CustomPointStyleEditor"; // For new 'custom-dual-value' maps
import PipelinePointStyleEditor from "./PipelinePointStyleEditor";
import LabelEditor from "./LabelEditor"; // Import the new LabelEditor component
import { mapConfigurationsAPI } from "../../services/api";
import { getColorFromScheme } from "./heatMapGenerator"; // Import color scheme for heat maps
import { initialLayerConfigurations } from "./mapConfig"; // Import default configurations for reset

// Helper function to safely get properties, avoiding errors on null/undefined
const getConfigProp = (config, propPath, defaultValue = null) => {
  if (!config) return defaultValue;
  const pathParts = propPath.split(".");
  let current = config;
  for (const part of pathParts) {
    if (current === null || current === undefined) return defaultValue;
    current = current[part];
  }
  return current !== undefined ? current : defaultValue;
};

const valueFormats = {
  income: { prefix: "$", decimals: 0, multiplier: 1 },
  homeValue: { prefix: "$", decimals: 0, multiplier: 1 },
  growth: { prefix: "", suffix: "%", decimals: 1, multiplier: 1 },
  density: { prefix: "", suffix: "/sq mi", decimals: 0, multiplier: 1 },
  age: { prefix: "", suffix: " yrs", decimals: 1, multiplier: 1 },
  unemployment: { prefix: "", suffix: "%", decimals: 1, multiplier: 1 },
  affordability: { prefix: "", decimals: 0, multiplier: 1 },
  totalPopulation: { prefix: "", decimals: 0, multiplier: 1 },
  totalHouseholds: { prefix: "", decimals: 0, multiplier: 1 },
  // Add more formats as needed for your custom data
  default: { prefix: "", suffix: "", decimals: 2, multiplier: 1 },
};

// Note: Heat map color scheme is now imported from heatMapGenerator.js
// The getColorFromScheme function provides the proper 7-level purple-to-red
// heat map palette with varying opacities that matches initial break generation

/**
 * Generates evenly distributed class breaks from layer data using equal intervals
 * This ensures each break has a distinct range that displays correctly
 * @param {Object} activeLayer - The ArcGIS layer to query
 * @param {string} fieldName - The field to analyze
 * @param {number} numBreaks - Number of breaks to generate
 * @param {Object} format - Value format configuration
 * @returns {Promise<Array>} Array of class break objects
 */
const generateEvenBreaksFromLayer = async (activeLayer, fieldName, numBreaks, format) => {
  if (!activeLayer || !fieldName) {
    console.warn('[LayerPropsEditor] Cannot generate breaks: missing layer or field');
    return null;
  }

  try {
    let values = [];

    // Check if this is a GraphicsLayer (has graphics collection) or FeatureLayer
    if (activeLayer.graphics && activeLayer.graphics.length > 0) {
      // GraphicsLayer - extract values directly from graphics
      console.log(`[LayerPropsEditor] Extracting values from GraphicsLayer (${activeLayer.graphics.length} graphics)`);
      activeLayer.graphics.forEach(graphic => {
        const value = graphic.attributes?.[fieldName];
        if (typeof value === 'number' && isFinite(value)) {
          values.push(value);
        }
      });
    } else if (typeof activeLayer.createQuery === 'function') {
      // FeatureLayer - use query
      console.log('[LayerPropsEditor] Querying FeatureLayer for values');
      const query = activeLayer.createQuery();
      query.where = "1=1";
      query.outFields = [fieldName];
      query.returnGeometry = false;

      const result = await activeLayer.queryFeatures(query);
      values = result.features
        .map(f => parseFloat(f.attributes[fieldName]))
        .filter(v => !isNaN(v) && v !== null);
    } else {
      console.warn('[LayerPropsEditor] Layer type not supported for break generation');
      return null;
    }

    if (values.length === 0) {
      console.warn('[LayerPropsEditor] No valid values found for field:', fieldName);
      return null;
    }

    console.log(`[LayerPropsEditor] Found ${values.length} values for ${fieldName}, generating ${numBreaks} breaks`);

    const sortedValues = [...values].sort((a, b) => a - b);
    const min = sortedValues[0];
    const max = sortedValues[sortedValues.length - 1];
    const range = max - min;

    // Format helper
    const prefix = format?.prefix || '';
    const suffix = format?.suffix || '';
    const decimals = format?.decimals ?? 0;

    const formatValue = (val) => {
      const formatted = decimals === 0 ? Math.round(val).toLocaleString() : val.toFixed(decimals);
      return `${prefix}${formatted}${suffix}`;
    };

    // Smart rounding to ensure distinct break values
    const smartRound = (val, dataRange) => {
      if (dataRange <= 0) return val;
      // Round to appropriate precision based on data range
      const magnitude = Math.pow(10, Math.floor(Math.log10(dataRange / numBreaks)));
      return Math.round(val / magnitude) * magnitude;
    };

    // Use equal-interval breaks to ensure distinct ranges
    const breaks = [];
    const intervalSize = range / numBreaks;

    for (let i = 0; i < numBreaks; i++) {
      // Calculate raw break boundaries
      let rangeMin = i === 0 ? min : min + (i * intervalSize);
      let rangeMax = i === numBreaks - 1 ? max : min + ((i + 1) * intervalSize);

      // Apply smart rounding for cleaner display values (except for first min and last max)
      if (i > 0) {
        rangeMin = smartRound(rangeMin, range);
      }
      if (i < numBreaks - 1) {
        rangeMax = smartRound(rangeMax, range);
      }

      // Ensure breaks don't have same min/max after rounding
      if (rangeMin === rangeMax && i > 0) {
        // Adjust to create a small gap
        const adjustment = Math.pow(10, -decimals) || 1;
        rangeMax = rangeMin + adjustment;
      }

      // Create label based on position
      let label;
      if (i === 0 && numBreaks > 1) {
        label = `Less than ${formatValue(rangeMax)}`;
      } else if (i === numBreaks - 1 && numBreaks > 1) {
        label = `${formatValue(rangeMin)} or more`;
      } else {
        label = `${formatValue(rangeMin)} - ${formatValue(rangeMax)}`;
      }

      // Use the same color scheme as heat map generator for consistency
      const colorArray = getColorFromScheme(i, numBreaks);

      breaks.push({
        minValue: i === 0 ? -Infinity : rangeMin,
        maxValue: i === numBreaks - 1 ? Infinity : rangeMax,
        label,
        symbol: {
          type: "simple-fill",
          style: "solid",
          color: [...colorArray], // Clone the color array
          outline: {
            color: 'rgba(255, 255, 255, 0.5)',
            width: 0.5
          }
        },
        // Preserve opacity metadata for the ColorBreakEditor
        preserveOpacity: true,
        originalOpacity: colorArray[3],
        hasCustomOpacities: true,
        colorSchemeLevel: `level${Math.min(i + 1, 7)}`
      });
    }

    // Ensure breaks are continuous (no gaps)
    for (let i = 1; i < breaks.length; i++) {
      breaks[i].minValue = breaks[i - 1].maxValue;
    }

    console.log(`[LayerPropsEditor] Generated ${breaks.length} equal-interval breaks from range ${min} to ${max}`);
    return breaks;
  } catch (error) {
    console.error('[LayerPropsEditor] Error generating breaks from layer:', error);
    return null;
  }
};

const LayerPropertiesEditor = ({
  isOpen,
  onClose,
  visualizationType,
  layerConfig,
  initialLayerConfigurations = {}, // Provide a default empty object to prevent errors
  onConfigChange,
  selectedAreaType,
  onPreview,
  projectId,
  activeTab,
  tabs,
  mapView, // Added for label editing
  labelManager, // Added for label editing
  isLabelEditMode = false, // Track if we're in label editing mode
  onLabelEditModeChange, // Callback to toggle label edit mode
  activeLayer, // Current active layer for label editing
}) => {
  const [currentConfig, setCurrentConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null); // Store original config for cancel functionality
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [effectiveType, setEffectiveType] = useState(null);
  const [rendererType, setRendererType] = useState(null); // To distinguish custom map subtypes
  const [previousLabelMode, setPreviousLabelMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if changes were made
  const labelStateRef = useRef({ editedLabels: new Map() });
  const originalConfigCapturedRef = useRef(false); // Track if we've captured the original config for this session

  // Log received initial configurations for debugging
  useEffect(() => {
    console.log(
      "LayerPropsEditor: Received initialLayerConfigurations:",
      initialLayerConfigurations
    );
    if (
      !initialLayerConfigurations ||
      typeof initialLayerConfigurations !== "object"
    ) {
      console.warn(
        "LayerPropsEditor: initialLayerConfigurations is missing or not an object. Defaulting to {}. Ensure it's passed correctly from the parent."
      );
    }
  }, [initialLayerConfigurations]);

  // --- Determine Effective Type and Renderer Type Logic ---
  useEffect(() => {
    console.log("LayerPropsEditor: Determining effectiveType/rendererType...", {
      vizProp: visualizationType,
      configType: layerConfig?.type,
      configRendererType: layerConfig?.rendererType,
      initialConfigs: initialLayerConfigurations,
      hasValueColumn1: !!layerConfig?.valueColumn1,
      hasValueColumn2: !!layerConfig?.valueColumn2,
      hasColorClassBreakInfos: !!layerConfig?.colorClassBreakInfos?.length,
      hasSizeClassBreakInfos: !!layerConfig?.sizeClassBreakInfos?.length,
      hasSizeInfos: !!layerConfig?.sizeInfos?.length,
      hasClassBreakInfos: !!layerConfig?.classBreakInfos?.length,
      configKeys: layerConfig ? Object.keys(layerConfig) : [],
    });
    let determinedType = null;
    let determinedRendererType = layerConfig?.rendererType || null;

    // Prioritize config's explicit type
    if (layerConfig?.type) {
      determinedType = layerConfig.type;
    } else if (visualizationType) {
      determinedType = visualizationType;
    }

    // Normalize common aliases
    if (determinedType === "pipeline") determinedType = "pipe";
    if (determinedType === "comps") determinedType = "comp";

    // Handle geography-dotdensity separately - route to dot-density editor
    if (determinedType === "geography-dotdensity" || layerConfig?.visualizationType === "dotdensity" || layerConfig?.isDotDensity) {
      console.log(`LayerPropsEditor: Detected geography dot density map, routing to dot-density editor`);
      determinedRendererType = "dot-density";
      determinedType = "dot-density";
    }
    // Normalize other geography types to class-breaks for editor
    else if (determinedType === "geography" || determinedType === "geography-heatmap" || determinedType === "geography-value") {
      console.log(`LayerPropsEditor: Normalizing geography type '${determinedType}' to 'class-breaks' for editor`);
      determinedRendererType = "class-breaks";
      // Keep the original type for reference but use class-breaks editor
      determinedType = "class-breaks";
    }

    // IMPORTANT: Override renderer type if we detect a 2-variable custom map
    // This check runs REGARDLESS of whether rendererType is already set
    if (determinedType === "custom") {
      // Check for 2-variable indicators
      const hasValueColumn2 = !!getConfigProp(layerConfig, "valueColumn2");
      const hasSizeBreaks = getConfigProp(layerConfig, "sizeClassBreakInfos.length", 0) > 0 ||
                           getConfigProp(layerConfig, "sizeInfos.length", 0) > 0;
      const hasColorBreaks = getConfigProp(layerConfig, "colorClassBreakInfos.length", 0) > 0;

      if (hasValueColumn2 || (hasColorBreaks && hasSizeBreaks)) {
        console.log("LayerPropsEditor: Detected 2-variable custom map, overriding rendererType to custom-dual-value");
        determinedRendererType = "custom-dual-value";
      }
    }

    // Further refinement based on structure or initial configurations
    if (determinedType) {
      if (determinedType.endsWith("_HEAT")) {
        determinedType = "class-breaks";
      } else if (
        initialLayerConfigurations &&
        initialLayerConfigurations[determinedType]?.type
      ) {
        console.log(
          `LayerPropsEditor: Mapping type '${determinedType}' using initialLayerConfigurations to '${initialLayerConfigurations[determinedType].type}'`
        );
        // If determinedType is a key in initialConfigs, its 'type' property is the renderer type
        // but the overall effectiveType remains the key itself (e.g., 'income_HEAT' -> effectiveType 'income_HEAT', renderer 'class-breaks')
        // For custom maps, we handle this differently.
        if (determinedType !== 'custom') { // Avoid overriding 'custom' if it came from vizProp or config.type
             if (!determinedRendererType) determinedRendererType = initialLayerConfigurations[determinedType].type;
        }
      }
    }
    
    // Infer type and renderer from config structure if still ambiguous
    if (
      !determinedType ||
      ["vector-tile", "feature"].includes(determinedType) || // Generic types that need more info
      (determinedType === 'custom' && !determinedRendererType) // 'custom' type but no specific renderer yet
    ) {
      // Check for custom dual value maps (has both colorClassBreakInfos and sizeClassBreakInfos/sizeInfos)
      if (getConfigProp(layerConfig, "colorClassBreakInfos.length", 0) > 0 &&
          (getConfigProp(layerConfig, "sizeClassBreakInfos.length", 0) > 0 || getConfigProp(layerConfig, "sizeInfos.length", 0) > 0)) {
          determinedType = "custom";
          determinedRendererType = "custom-dual-value";
      }
      // Check for 2-variable custom maps with both valueColumn1 and valueColumn2
      else if (getConfigProp(layerConfig, "valueColumn1") && getConfigProp(layerConfig, "valueColumn2")) {
          determinedType = "custom";
          determinedRendererType = "custom-dual-value";
      }
      // Legacy check for custom dual value markers
      else if (getConfigProp(layerConfig, "classBreakInfos.length", 0) > 0 && getConfigProp(layerConfig, "valueColumn1")) {
          determinedType = "custom";
          determinedRendererType = "custom-dual-value";
      } else if (getConfigProp(layerConfig, "classBreakInfos.length", 0) > 0) {
        determinedType = determinedType || "class-breaks"; // Keep 'custom' if it was, otherwise 'class-breaks'
        determinedRendererType = "class-breaks";
      } else if (getConfigProp(layerConfig, "uniqueValueInfos.length", 0) > 0) {
        determinedType = determinedType || "unique-value";
        determinedRendererType = "unique-value";
      } else if (
        getConfigProp(layerConfig, "attributes.length", 0) > 0 &&
        getConfigProp(layerConfig, "dotValue") !== undefined
      ) {
        determinedType = "dot-density";
        determinedRendererType = "dot-density";
      } else if (
        getConfigProp(layerConfig, "symbol") &&
        getConfigProp(layerConfig, "customData.data")
      ) {
        // This is a point layer. Could be 'comp', 'pipe', or a simpler 'custom' (single symbol).
        // If determinedType was already 'comp' or 'pipe', keep it.
        if (!["comp", "pipe"].includes(determinedType)) {
          determinedType = "custom";
        }
        // If no specific renderer, assume simple point editor
        if (!determinedRendererType && (determinedType === 'custom' || determinedType === 'comp')) {
            determinedRendererType = "simple"; // For PointStyleEditor
        }
      }
    }
    
    // If it's a 'comp' or '1varmap' map, ensure renderer is 'classBreaks' if breaks exist, else 'simple'
    if ((determinedType === 'comp' || determinedType === '1varmap') && !determinedRendererType) {
        if (getConfigProp(layerConfig, "classBreakInfos.length", 0) > 0) {
            determinedRendererType = "classBreaks";
        } else {
            determinedRendererType = "simple";
        }
    }
    // If it's a 'pipe' map, it uses unique value rendering based on status
    if (determinedType === 'pipe' && !determinedRendererType) {
        determinedRendererType = "uniqueValue"; // PipelinePointStyleEditor handles this
    }


    setEffectiveType(determinedType);
    setRendererType(determinedRendererType);
    console.log(
      `LayerPropsEditor: Determined effectiveType: ${determinedType}, rendererType: ${determinedRendererType}`,
      { vizProp: visualizationType, configType: layerConfig?.type, configRenderer: layerConfig?.rendererType }
    );

  }, [visualizationType, layerConfig, initialLayerConfigurations]);

  useEffect(() => {
    setIsTransitioning(true);
    let initialConfig = layerConfig
      ? JSON.parse(JSON.stringify(layerConfig))
      : null;

    if (initialConfig) {
        if (!initialConfig.type && effectiveType) {
            initialConfig.type = effectiveType;
        }
        if (!initialConfig.rendererType && rendererType) {
            initialConfig.rendererType = rendererType;
        }
    } else if (effectiveType) {
        initialConfig = { type: effectiveType };
        if (rendererType) initialConfig.rendererType = rendererType;
    } else {
      console.warn(
        "LayerPropsEditor: Could not determine effective type/renderer and no layerConfig provided. Setting currentConfig to null."
      );
      initialConfig = null;
    }

    setCurrentConfig(initialConfig);

    // FIXED: Only capture original config once when panel opens or when layerConfig prop actually changes
    // Don't overwrite originalConfig when only effectiveType/rendererType change during editing
    if (!originalConfigCapturedRef.current || !hasUnsavedChanges) {
      console.log("[LayerPropertiesEditor] Capturing original config for cancel functionality");
      setOriginalConfig(initialConfig ? JSON.parse(JSON.stringify(initialConfig)) : null);
      setHasUnsavedChanges(false);
      originalConfigCapturedRef.current = true;
    }

    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 650);

    return () => clearTimeout(timer);
  }, [layerConfig, visualizationType, effectiveType, rendererType]);

  // Handle label edit mode changes
  useEffect(() => {
    // Track transitions between modes to handle cleanup
    if (previousLabelMode !== isLabelEditMode) {
      console.log(
        `[LayerPropertiesEditor] Label edit mode changed: ${previousLabelMode} -> ${isLabelEditMode}`
      );

      if (isLabelEditMode) {
        // Entering label edit mode, save the state
        labelStateRef.current.editMode = true;
      } else {
        // Exiting label edit mode - ensure any pending changes are applied
        if (labelManager && labelStateRef.current.editMode) {
          console.log(
            "[LayerPropertiesEditor] Exiting label edit mode, ensuring label cache is up to date"
          );

          // Force a refresh of all labels to ensure any final changes are applied
          setTimeout(() => {
            try {
              if (labelManager.refreshLabels) {
                labelManager.refreshLabels(); // Refresh all labels
              }
            } catch (error) {
              console.warn(
                "[LayerPropertiesEditor] Error refreshing labels on mode exit:",
                error
              );
            }
          }, 0);
        }

        // Reset label edit state
        labelStateRef.current.editMode = false;
      }

      setPreviousLabelMode(isLabelEditMode);
    }
  }, [isLabelEditMode, labelManager, previousLabelMode]);

  const handleConfigChange = (newConfig) => {
    if (!newConfig) return;
    console.log("LayerPropsEditor: handleConfigChange", newConfig);

    // FIX: Flatten nested classBreakInfos if present (prevents nested classBreakInfos.classBreakInfos structure)
    let flattenedConfig = { ...newConfig };
    if (newConfig.classBreakInfos && typeof newConfig.classBreakInfos === 'object' && newConfig.classBreakInfos.classBreakInfos) {
      console.log("[LayerPropsEditor] Flattening nested classBreakInfos structure");
      flattenedConfig.classBreakInfos = newConfig.classBreakInfos.classBreakInfos;
      // Preserve numberFormat if it exists
      if (newConfig.classBreakInfos.numberFormat) {
        flattenedConfig.numberFormat = newConfig.classBreakInfos.numberFormat;
      }
    }

    if (flattenedConfig.type && flattenedConfig.type !== effectiveType) {
      setEffectiveType(flattenedConfig.type);
    }
    if (flattenedConfig.rendererType && flattenedConfig.rendererType !== rendererType) {
      setRendererType(flattenedConfig.rendererType);
    }
    setCurrentConfig(flattenedConfig);
    setHasUnsavedChanges(true); // Mark as having unsaved changes
    if (onConfigChange) onConfigChange(flattenedConfig);
    // Add preserveLabels flag to prevent label removal during preview
    if (onPreview) onPreview({ ...flattenedConfig, preserveLabels: true });
  };

  const handleLabelEditModeChange = (newMode) => {
    if (onLabelEditModeChange) {
      if (isLabelEditMode && !newMode && labelManager) {
        try {
          console.log("[LayerPropertiesEditor] Saving labels on mode change");
          if (labelManager.savePositions) labelManager.savePositions(true); // Force save
        } catch (error) {
          console.warn("[LayerPropertiesEditor] Error saving labels:", error);
        }
      }
      onLabelEditModeChange(newMode);
    }
  };


/**
 * FIXED: Ensures class break infos maintain their absolute bounds for proper UI display AND ArcGIS rendering
 * This prevents the ColorBreakEditor from showing truncated ranges and ensures ArcGIS gets proper maxValues
 */
const validateClassBreaksForDisplay = (classBreakInfos, fieldName) => {
  if (!classBreakInfos || !Array.isArray(classBreakInfos) || classBreakInfos.length === 0) {
    return classBreakInfos;
  }

  console.log("[LayerPropertiesEditor] FIXED: Validating class breaks for display:", {
    fieldName,
    originalBreaks: classBreakInfos.map(b => `${b.minValue} to ${b.maxValue} (${b.label})`)
  });

  // Create a copy to avoid mutating the original
  const validatedBreaks = classBreakInfos.map((breakInfo, index) => {
    const isLastBreak = index === classBreakInfos.length - 1;
    let validatedBreak = { ...breakInfo };

    // CRITICAL FIX: Handle the last break maxValue issue
    if (isLastBreak && (breakInfo.maxValue === null || breakInfo.maxValue === undefined)) {
      // Try to get the absolute max from various sources
      let absoluteMax = 100; // Default fallback

      // Check if we have absolute bounds stored in the break metadata
      if (breakInfo._absoluteMax) {
        absoluteMax = breakInfo._absoluteMax;
      }
      // Check if we have dataStats with absolute bounds
      else if (breakInfo.dataStats && breakInfo.dataStats.absoluteMax) {
        absoluteMax = breakInfo.dataStats.absoluteMax;
      }
      // Check if there's an absoluteMax property (from the fixed heatMapGenerator)
      else if (breakInfo.absoluteMax) {
        absoluteMax = breakInfo.absoluteMax;
      }
      // Check if we have value format info to determine appropriate max
      else if (breakInfo.valueFormat) {
        const format = breakInfo.valueFormat;
        if (format.suffix === ' yrs') {
          absoluteMax = 100; // Age
        } else if (format.suffix === '%') {
          absoluteMax = 100; // Percentage
        } else if (format.prefix === '$') {
          absoluteMax = fieldName?.toLowerCase().includes('income') ? 1000000 : 5000000;
        } else {
          // For other types, use a reasonable multiplier of the previous break's max
          const previousBreak = classBreakInfos[index - 1];
          if (previousBreak && previousBreak.maxValue) {
            absoluteMax = Math.max(previousBreak.maxValue * 3, 50000);
          }
        }
      }

      // FIXED: Always set a real maxValue for ArcGIS renderer
      validatedBreak.maxValue = absoluteMax;
      validatedBreak._displayMaxValue = absoluteMax; // Store for reference
      validatedBreak._wasFixedFromNull = true; // Track that this was fixed
      
      console.log(`[LayerPropertiesEditor] FIXED: Set real maxValue for last break: ${absoluteMax} (was null)`);
    }

    // Ensure first break starts at appropriate minimum
    if (index === 0 && (breakInfo.minValue === null || breakInfo.minValue === undefined)) {
      validatedBreak.minValue = 0;
      validatedBreak._displayMinValue = 0;
      validatedBreak._wasFixedFromNull = true;
    }

    // Store additional metadata for debugging
    if (isLastBreak) {
      validatedBreak._isLastBreak = true;
      validatedBreak._originalMaxValue = breakInfo.maxValue; // Preserve original
    }

    return validatedBreak;
  });

  console.log("[LayerPropertiesEditor] FIXED: Validated breaks for display:", {
    validatedBreaks: validatedBreaks.map(b => {
      const fixedNote = b._wasFixedFromNull ? ' (FIXED)' : '';
      return `${b.minValue} to ${b.maxValue} (${b.label})${fixedNote}`;
    })
  });

  return validatedBreaks;
};


  /**
   * Cancel changes and revert to original configuration
   * Reverts the map preview to the original state before any edits were made
   */
  const handleCancel = () => {
    console.log("[LayerPropertiesEditor] Cancel clicked - reverting to original config");

    if (isLabelEditMode && labelManager) {
      try {
        console.log(
          "[LayerPropertiesEditor] Finalizing label edits before closing..."
        );
        if (labelManager.savePositions) {
          const result = labelManager.savePositions(true);
          if (result.success || result.count > 0) { // Check for success or actual saves
            console.log(
              `[LayerPropertiesEditor] Saved ${result.count} label positions before closing`
            );
          }
        }
        if (labelManager.loadPositions) {
          labelManager.loadPositions(true, false, true); // force, don't preserve map, do preserve storage
        }
        if (labelManager.refreshLabels) {
          labelManager.refreshLabels([], true);
        }
      } catch (error) {
        console.error(
          "[LayerPropertiesEditor] Error finalizing label edits before close:",
          error
        );
      }
    } else if (hasUnsavedChanges && originalConfig) {
      // Revert to original configuration - update the map preview
      console.log("[LayerPropertiesEditor] Reverting to original config:", originalConfig);
      if (onPreview) {
        // Add preserveLabels flag to prevent label removal during revert preview
        onPreview({ ...originalConfig, preserveLabels: true });
      }
      if (onConfigChange) {
        onConfigChange(originalConfig);
      }
      setCurrentConfig(originalConfig);
      setHasUnsavedChanges(false);
    }

    // Reset the original config capture ref for next session
    originalConfigCapturedRef.current = false;

    if (onClose) {
      onClose();
    }
  };

  const handleSave = async () => {
    if (!currentConfig || !effectiveType || isSaving) {
      console.warn(
        "[Save] Save aborted. Missing currentConfig, effectiveType, or already saving.",
        { currentConfig, effectiveType, rendererType, isSaving }
      );
      if (!effectiveType)
        alert("Cannot save: Layer type could not be determined.");
      return;
    }

    setIsSaving(true);
    console.log(
      "[Save] Initiated save with config:",
      currentConfig,
      "effectiveType:", effectiveType, "rendererType:", rendererType
    );

    try {
      // Preserve original geography type if it was normalized to class-breaks
      const originalType = visualizationType;
      const isGeographyType = originalType === "geography" || originalType === "geography-heatmap" || originalType === "geography-value";
      const typeToSave = isGeographyType ? originalType : effectiveType;

      const configToSave = {
        ...currentConfig,
        type: typeToSave,
        rendererType: rendererType || currentConfig.rendererType, // Ensure rendererType is also included
      };

      const activeTabData = tabs.find((tab) => tab.id === activeTab);
      // ... (rest of the save logic, ensuring configToSave.rendererType is passed along if relevant for backend)

      const configurationsToSave = tabs
        .filter((tab) => tab.id !== 1) // Exclude Core Map
        .map((tab, index) => {
          const isActive = tab.id === activeTab;
          const baseLayerConfig = isActive
            ? configToSave
            : tab.layerConfiguration;
          const baseVizType =
            tab.visualizationType || (isActive ? visualizationType : null); 

          let finalLayerConfig = baseLayerConfig ? { ...baseLayerConfig } : {};

          // CRITICAL: Strip large cached data that shouldn't be sent to the API
          // enrichedFeatures contains full geometry data (polygon rings, coordinates) which can be megabytes
          // This data is only needed for local caching to avoid re-enrichment, not for database storage
          delete finalLayerConfig.enrichedFeatures;
          delete finalLayerConfig.cachedFeatures;
          delete finalLayerConfig.rawFeatures;
          delete finalLayerConfig.geometryData;

          let configType = finalLayerConfig.type;
          let configRendererType = finalLayerConfig.rendererType;

          if (!configType && baseVizType) {
            configType = baseVizType;
            if (configType === "pipeline") configType = "pipe";
            if (configType === "comps") configType = "comp";
            if (configType.endsWith("_HEAT")) configType = "class-breaks";
            if (initialLayerConfigurations && initialLayerConfigurations[configType]?.type) {
              if (!configRendererType) configRendererType = initialLayerConfigurations[configType].type;
            }
          }
          
          if (!configType) { /* ... infer type ... */ }
          if (configType === 'custom' && !configRendererType) { // Special handling for custom
              if (getConfigProp(finalLayerConfig, "colorClassBreakInfos") && getConfigProp(finalLayerConfig, "sizeInfos")) {
                configRendererType = "custom-dual-value";
              } else if (getConfigProp(finalLayerConfig, "classBreakInfos")) {
                configRendererType = "classBreaks"; // Could be simple custom with class breaks
              } else {
                configRendererType = "simple"; // Default simple custom
              }
          } else if ((configType === 'comp' || configType === '1varmap') && !configRendererType) {
              configRendererType = getConfigProp(finalLayerConfig, "classBreakInfos.length", 0) > 0 ? "classBreaks" : "simple";
          } else if (configType === 'pipe' && !configRendererType) {
              configRendererType = "uniqueValue";
          }

          // For comps and 1varmap, ensure field property is set to the actual value column
          if (configType === 'comp' || configType === '1varmap') {
            const valueColumn = finalLayerConfig.valueColumn || finalLayerConfig.valueColumn1;
            if (valueColumn) {
              finalLayerConfig.field = valueColumn;
            }
          }

          finalLayerConfig.type = configType;
          finalLayerConfig.rendererType = configRendererType;

          return {
            id: tab.configId,
            project_id: projectId,
            project: projectId,
            tab_name: tab.name,
            visualization_type: baseVizType || "", 
            area_type: convertAreaTypeToString(tab.areaType),
            layer_configuration: finalLayerConfig, 
            order: index,
          };
        })
        .filter(Boolean);
      
      // ... (API interaction logic - delete existing, create new)
      console.log("[Save] Configurations prepared for API:", configurationsToSave);

      // API Interaction (Simplified for brevity)
      const existingConfigsResponse = await mapConfigurationsAPI.getAll(projectId);
      const existingConfigs = existingConfigsResponse?.data?.results || existingConfigsResponse?.data || [];
      if (Array.isArray(existingConfigs) && existingConfigs.length > 0) {
        const configsToDelete = existingConfigs.filter(cfg => cfg.tab_name !== "Core Map");
        await Promise.all(configsToDelete.map(config => mapConfigurationsAPI.delete(config.id)));
      }

      if (configurationsToSave.length > 0) {
        const createPromises = configurationsToSave.map(config =>
          mapConfigurationsAPI.create(projectId, config).then(response => ({
            tabName: config.tab_name,
            configId: response.data.id,
            originalTabId: tabs.find(t => t.name === config.tab_name)?.id,
            newConfig: config.layer_configuration
          }))
        );
        const creationResults = (await Promise.all(createPromises)).filter(r => r);

        if (creationResults.length > 0) {
          window.dispatchEvent(new CustomEvent("mapConfigsSaved", { detail: { successfulCreations: creationResults } }));
          const activeTabSaveResult = creationResults.find(r => tabs.find(t => t.id === activeTab)?.name === r.tabName);
          if (activeTabSaveResult) {
            onConfigChange(activeTabSaveResult.newConfig);
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent("refreshVisualization", { detail: { tabId: activeTab, config: activeTabSaveResult.newConfig } }));
            }, 50);
          }
        } else {
           alert("Failed to save configurations. Changes applied locally only.");
           onConfigChange(configToSave);
        }
      } else {
        onConfigChange(configToSave); // Apply local if nothing to save to backend
      }

      setHasUnsavedChanges(false); // Reset unsaved changes flag
      originalConfigCapturedRef.current = false; // Reset for next session
      onClose();
    } catch (error) {
      console.error("[Save] General error during save process:", error.response?.data || error.message || error);
      alert("Failed to save map configurations. Check console for details.");
    } finally {
      setIsSaving(false);
    }
  };

  const convertAreaTypeToString = (areaTypeValue) => {
    const value = areaTypeValue?.value !== undefined ? areaTypeValue.value : areaTypeValue;
    if (typeof value === "string") {
      const lowerValue = value.toLowerCase();
      // Handle common frontend variations that need mapping
      if (lowerValue === 'block_group') return 'blockgroup';
      if (lowerValue === 'msa') return 'cbsa';

      // Valid area types matching backend MARKET_AREA_TYPES
      if (["radius", "drivetime", "zip", "county", "place", "tract", "block", "blockgroup", "cbsa", "state", "usa", "custom", "site_location"].includes(lowerValue)) {
        return lowerValue;
      }
      const numVal = parseInt(value);
      if (!isNaN(numVal)) {
        if (numVal === 12) return "tract";
        if (numVal === 11) return "county";
        if (numVal === 150) return "blockgroup";
      }
      return "custom";
    }
    if (typeof value === "number") {
      if (value === 12) return "tract";
      if (value === 11) return "county";
      if (value === 150) return "blockgroup";
      return "custom";
    }
    return "custom";
  };

  const renderEditor = () => {
    if (isLabelEditMode) {
      return (
        <LabelEditor
          isOpen={isOpen}
          onClose={() => {
            try {
              if (labelManager) {
                if (labelManager.savePositions) labelManager.savePositions(true);
                if (labelManager.loadPositions) labelManager.loadPositions(true, false, true);
                if (labelManager.refreshLabels) labelManager.refreshLabels([], true);
              }
            } catch (error) { console.warn("Error finalizing labels on close:", error); }
            if (onLabelEditModeChange) onLabelEditModeChange(false);
          }}
          mapView={mapView} labelManager={labelManager} activeLayer={activeLayer}
        />
      );
    }

    if (isTransitioning || !currentConfig) {
      return <div className="flex items-center justify-center h-full"><div className="text-gray-500 dark:text-gray-400">{isTransitioning ? "Loading configuration..." : "No configuration loaded or type undetermined."}</div></div>;
    }

    console.log("LayerPropsEditor: Rendering editor for effectiveType:", effectiveType, "rendererType:", rendererType);

    if (effectiveType === "pipe") { // Uses PipelinePointStyleEditor which handles unique value internally
      return <PipelinePointStyleEditor config={currentConfig} onChange={handleConfigChange} onPreview={onPreview} onClose={onClose} />;
    }
    
    // New: Custom map with dual-value rendering (color by Value1, size by Value2)
    if (effectiveType === "custom" && rendererType === "custom-dual-value") {
      // Ensure essential properties for CustomPointStyleEditor are present in currentConfig
      const configForCustomEditor = {
        ...currentConfig,
        symbol: currentConfig.symbol || { style: 'circle', outline: { color: '#FFFFFF', width: 0 }, minSize: 6, maxSize: 24 }, // Ensure symbol and min/max size exist
        colorClassBreakInfos: currentConfig.colorClassBreakInfos || [],
        // Support both sizeInfos and sizeClassBreakInfos
        sizeInfos: currentConfig.sizeInfos || currentConfig.sizeClassBreakInfos || [],
        sizeClassBreakInfos: currentConfig.sizeClassBreakInfos || currentConfig.sizeInfos || [],
        customData: currentConfig.customData || { data: [] } // Ensure customData and data array exist
      };
      return <CustomPointStyleEditor config={configForCustomEditor} onChange={handleConfigChange} onPreview={onPreview} valueFormats={valueFormats} />;
    }
    // Comps maps, 1varmap, or simple custom maps (single symbol or single value class breaks)
    if ((effectiveType === "comp") || (effectiveType === "1varmap") || (effectiveType === "custom" && (rendererType === "simple" || rendererType === "classBreaks"))) {
        // PointStyleEditor might need to handle classBreaks for comps/1varmap if valueColumn1 is used.
        // For now, it assumes simple symbol or that classBreakInfos are directly in config for comps/1varmap.
      return <PointStyleEditor config={currentConfig} onChange={handleConfigChange} onPreview={onPreview} mapType={effectiveType} onClose={onClose} />;
    }

    if (effectiveType === "dot-density" || rendererType === "dot-density") {
      const areaVal = selectedAreaType?.value !== undefined ? selectedAreaType.value : selectedAreaType;
      const isTract = areaVal === 12 || String(areaVal).toLowerCase() === "tract";
      const isCounty = areaVal === 11 || String(areaVal).toLowerCase() === "county";
      const isZip = String(areaVal).toLowerCase() === "zip";
      const defaultDotValue = isTract ? 10 : isCounty ? 500 : isZip ? 100 : 100;
      const currentDotValue = getConfigProp(currentConfig, "dotValue") !== null ? parseInt(String(getConfigProp(currentConfig, "dotValue")), 10) : defaultDotValue;
      let attributes = getConfigProp(currentConfig, "attributes", []);
      if (!Array.isArray(attributes) || attributes.length === 0) {
        const fieldName = getConfigProp(currentConfig, "field", "value");
        attributes = [{ field: fieldName, color: "#E60049", label: getConfigProp(currentConfig, "label", fieldName) }];
      }
      const configForEditor = {
        ...currentConfig,
        type: "dot-density",
        dotValue: currentDotValue,
        attributes: attributes,
        // Explicitly preserve geography dot density flags for createLayers routing
        isDotDensity: currentConfig.isDotDensity,
        areaType: currentConfig.areaType
      };
      return <DotDensityEditor config={configForEditor} onChange={handleConfigChange} selectedAreaType={selectedAreaType} onPreview={onPreview} />;
    }

    if (effectiveType === "class-breaks" || rendererType === "class-breaks") {
      const originalBreaks = getConfigProp(currentConfig, "classBreakInfos", []);
      
      // VALIDATE BREAKS FOR PROPER DISPLAY
      const validatedBreaks = validateClassBreaksForDisplay(originalBreaks, 
        getConfigProp(currentConfig, "field", "") || 
        getConfigProp(currentConfig, "visualizationKey", "")?.replace("_HEAT", "") || 
        visualizationType?.replace("_HEAT", "")
      );
      
      // COMPREHENSIVE DEBUG LOGGING - Let's see what's actually in the configuration
      console.log("LayerPropsEditor: === COMPREHENSIVE FORMAT DETECTION DEBUG ===");
      console.log("LayerPropsEditor: currentConfig structure:", {
        hasValueFormat: !!currentConfig.valueFormat,
        valueFormat: currentConfig.valueFormat,
        field: currentConfig.field,
        visualizationKey: currentConfig.visualizationKey,
        keys: Object.keys(currentConfig),
        hasClassBreakInfos: !!currentConfig.classBreakInfos,
        classBreakInfosLength: currentConfig.classBreakInfos?.length || 0
      });
      
      if (validatedBreaks.length > 0) {
        console.log("LayerPropsEditor: First break structure:", {
          hasValueFormat: !!validatedBreaks[0].valueFormat,
          valueFormat: validatedBreaks[0].valueFormat,
          hasDataStats: !!validatedBreaks[0].dataStats,
          dataStats: validatedBreaks[0].dataStats,
          label: validatedBreaks[0].label,
          minValue: validatedBreaks[0].minValue,
          maxValue: validatedBreaks[0].maxValue,
          keys: Object.keys(validatedBreaks[0])
        });
        
        console.log("LayerPropsEditor: Last break structure:", {
          label: validatedBreaks[validatedBreaks.length - 1].label,
          minValue: validatedBreaks[validatedBreaks.length - 1].minValue,
          maxValue: validatedBreaks[validatedBreaks.length - 1].maxValue,
          _displayMaxValue: validatedBreaks[validatedBreaks.length - 1]._displayMaxValue
        });
      }
      
      // ENHANCED FORMAT DETECTION with comprehensive priority system
      let format = valueFormats.default;
      let formatSource = 'default';
      
      // Priority 1: Check if format is stored directly in the currentConfig (from map generation)
      if (currentConfig.valueFormat && typeof currentConfig.valueFormat === 'object' && currentConfig.valueFormat.prefix !== undefined) {
        format = currentConfig.valueFormat;
        formatSource = 'currentConfig.valueFormat';
        console.log("LayerPropsEditor: ✅ Using stored valueFormat from currentConfig:", format);
      }
      // Priority 2: Check if any classBreakInfo has valueFormat stored
      else if (validatedBreaks.length > 0 && validatedBreaks[0].valueFormat && typeof validatedBreaks[0].valueFormat === 'object' && validatedBreaks[0].valueFormat.prefix !== undefined) {
        format = validatedBreaks[0].valueFormat;
        formatSource = 'breaks[0].valueFormat';
        console.log("LayerPropsEditor: ✅ Using valueFormat from first classBreakInfo:", format);
      }
      // Priority 3: Check if any break has dataStats with valueFormat
      else if (validatedBreaks.length > 0 && validatedBreaks[0].dataStats && validatedBreaks[0].dataStats.valueFormat && typeof validatedBreaks[0].dataStats.valueFormat === 'object' && validatedBreaks[0].dataStats.valueFormat.prefix !== undefined) {
        format = validatedBreaks[0].dataStats.valueFormat;
        formatSource = 'breaks[0].dataStats.valueFormat';
        console.log("LayerPropsEditor: ✅ Using valueFormat from dataStats:", format);
      }
      // Priority 4: Try to detect format from existing labels (if they have $ or % signs)
      else if (validatedBreaks.length > 0) {
        const sampleLabels = validatedBreaks.slice(0, 3).map(b => b.label || '').join(' ');
        console.log("LayerPropsEditor: Analyzing sample labels for format clues:", sampleLabels);
        
        if (sampleLabels.includes('$')) {
          format = valueFormats.income;
          formatSource = 'detected from labels ($)';
          console.log("LayerPropsEditor: ✅ Detected currency format from labels");
        } else if (sampleLabels.includes('%')) {
          format = valueFormats.percentage;
          formatSource = 'detected from labels (%)';
          console.log("LayerPropsEditor: ✅ Detected percentage format from labels");
        } else if (sampleLabels.includes('/sq mi')) {
          format = valueFormats.density;
          formatSource = 'detected from labels (/sq mi)';
          console.log("LayerPropsEditor: ✅ Detected density format from labels");
        } else if (sampleLabels.includes(' yrs')) {
          format = valueFormats.age;
          formatSource = 'detected from labels (yrs)';
          console.log("LayerPropsEditor: ✅ Detected age format from labels");
        }
      }
      
      // Priority 5: Enhanced field name derivation
      if (formatSource === 'default') {
        const fieldName = getConfigProp(currentConfig, "field", "") || 
                        getConfigProp(currentConfig, "visualizationKey", "")?.replace("_HEAT", "") || 
                        visualizationType?.replace("_HEAT", "") || "";
        
        if (fieldName) {
          console.log("LayerPropsEditor: Attempting enhanced field name analysis for:", fieldName);
          
          // Enhanced field name analysis
          const formatKeyBase = fieldName.toLowerCase();
          
          // Age fields
          if (formatKeyBase.includes('age') || formatKeyBase.includes('medage')) {
            format = valueFormats.age;
            formatSource = `field analysis (age): ${fieldName}`;
          }
          // Income fields
          else if (formatKeyBase.includes('income') || formatKeyBase.includes('inc') || formatKeyBase.includes('earning')) {
            format = valueFormats.income;
            formatSource = `field analysis (income): ${fieldName}`;
          }
          // Percentage fields
          else if (formatKeyBase.includes('pct') || formatKeyBase.includes('percent') || formatKeyBase.includes('rate')) {
            format = valueFormats.percentage;
            formatSource = `field analysis (percentage): ${fieldName}`;
          }
          // Home value fields
          else if (formatKeyBase.includes('value') || formatKeyBase.includes('val')) {
            format = valueFormats.homeValue;
            formatSource = `field analysis (home value): ${fieldName}`;
          }
          // Population fields
          else if (formatKeyBase.includes('pop') || formatKeyBase.includes('population')) {
            format = valueFormats.totalPopulation;
            formatSource = `field analysis (population): ${fieldName}`;
          }
          // Household fields
          else if (formatKeyBase.includes('hh') || formatKeyBase.includes('household')) {
            format = valueFormats.totalHouseholds;
            formatSource = `field analysis (households): ${fieldName}`;
          }
          else {
            const formatKey = Object.keys(valueFormats).find((key) => formatKeyBase.includes(key.toLowerCase())) || "default";
            format = valueFormats[formatKey] || valueFormats.default;
            formatSource = `fallback field analysis (${fieldName} -> ${formatKey})`;
          }
          
          console.log("LayerPropsEditor: Field analysis result:", fieldName, "->", formatSource, "->", format);
        } else {
          console.log("LayerPropsEditor: No field name available for format detection");
        }
      }
      
      // FINAL VALIDATION AND LOGGING
      if (!format || typeof format !== 'object') {
        console.warn("LayerPropsEditor: Invalid format detected, using default:", format);
        format = valueFormats.default;
        formatSource = 'forced default (invalid format)';
      }
      
      console.log("LayerPropsEditor: === FINAL FORMAT SELECTION ===");
      console.log("LayerPropsEditor: Selected format:", format);
      console.log("LayerPropsEditor: Format source:", formatSource);
      console.log("LayerPropsEditor: === END FORMAT DETECTION DEBUG ===");
      
      // Create config with validated breaks
      const configForEditor = {
        ...currentConfig,
        type: "class-breaks",
        classBreakInfos: validatedBreaks // Use validated breaks with absolute bounds
      };

      // Handler to regenerate breaks evenly from layer data
      const handleRegenerateBreaks = async (newBreakCount) => {
        const fieldName = getConfigProp(currentConfig, "field", "");
        if (!activeLayer || !fieldName) {
          console.warn('[LayerPropsEditor] Cannot regenerate: missing layer or field');
          return;
        }

        console.log(`[LayerPropsEditor] Regenerating ${newBreakCount} breaks for field: ${fieldName}`);
        const newBreaks = await generateEvenBreaksFromLayer(activeLayer, fieldName, newBreakCount, format);

        if (newBreaks && newBreaks.length > 0) {
          const updatedBreaks = validateClassBreaksForDisplay(newBreaks, fieldName);
          handleConfigChange({ ...configForEditor, classBreakInfos: updatedBreaks });
        }
      };

      // Debug: Log all possible sources of field name for reset functionality
      const activeTabData = tabs?.find(t => t.id === activeTab);
      const tabConfig = activeTabData?.layerConfiguration;
      const originalFormData = activeTabData?.originalFormData;
      console.log("[LayerPropsEditor] Field sources for reset:", {
        "currentConfig.field": currentConfig?.field,
        "currentConfig.visualizationKey": currentConfig?.visualizationKey,
        "activeLayer?.legendInfo?.field": activeLayer?.legendInfo?.field,
        "activeLayer?.legendInfo?.visualizationKey": activeLayer?.legendInfo?.visualizationKey,
        "tabConfig?.field": tabConfig?.field,
        "tabConfig?.visualizationKey": tabConfig?.visualizationKey,
        "tabConfig?.originalVisualizationSelection": tabConfig?.originalVisualizationSelection,
        "originalFormData?.selectedVisualization": originalFormData?.selectedVisualization,
        "activeTabData?.visualizationType": activeTabData?.visualizationType,
        "visualizationType prop": visualizationType,
        "activeLayer exists": !!activeLayer,
        "activeLayer?.legendInfo exists": !!activeLayer?.legendInfo,
      });

      // Determine field name from multiple sources (prioritize most reliable)
      const resolvedFieldName =
        getConfigProp(currentConfig, "field", "") ||
        getConfigProp(currentConfig, "visualizationKey", "") ||
        activeLayer?.legendInfo?.field ||
        activeLayer?.legendInfo?.visualizationKey ||
        tabConfig?.field ||
        tabConfig?.visualizationKey ||
        tabConfig?.originalVisualizationSelection ||
        originalFormData?.selectedVisualization ||
        // If visualizationType is specific (contains _HEAT), extract field from it
        (activeTabData?.visualizationType?.includes('_HEAT') ? activeTabData.visualizationType.replace('_HEAT', '') : null) ||
        (visualizationType?.includes('_HEAT') ? visualizationType.replace('_HEAT', '') : null);

      console.log("[LayerPropsEditor] Resolved fieldName for reset:", resolvedFieldName);

      return (
        <ColorBreakEditor
          breaks={configForEditor.classBreakInfos}
          onBreaksChange={(newBreaks) => {
            // Handle both array and object formats from ColorBreakEditor
            // ColorBreakEditor sends { classBreakInfos, numberFormat, type } when number format changes
            let breaksArray = newBreaks;
            let numberFormatFromBreaks = null;

            if (newBreaks && !Array.isArray(newBreaks) && newBreaks.classBreakInfos) {
              breaksArray = newBreaks.classBreakInfos;
              numberFormatFromBreaks = newBreaks.numberFormat;
              console.log("[LayerPropertiesEditor] Extracted numberFormat from breaks:", numberFormatFromBreaks);
            }

            // When breaks change, preserve absolute bounds
            const updatedBreaks = validateClassBreaksForDisplay(breaksArray, getConfigProp(currentConfig, "field", ""));

            // Include numberFormat in the updated config if it was provided
            const updatedConfig = { ...configForEditor, classBreakInfos: updatedBreaks };
            if (numberFormatFromBreaks) {
              updatedConfig.numberFormat = numberFormatFromBreaks;
            }

            handleConfigChange(updatedConfig);
          }}
          visualizationType={visualizationType}
          valueFormat={format}
          onRegenerateBreaks={activeLayer ? handleRegenerateBreaks : null}
          initialLayerConfigurationsProp={initialLayerConfigurations}
          numberFormatProp={currentConfig?.numberFormat}
          fieldName={resolvedFieldName}
        />
      );
    }

    // Geography heat map editor - displays current configuration
    if (effectiveType === "geography-heatmap" || effectiveType === "geography-value" || effectiveType === "geography") {
      const isValueBased = effectiveType === "geography-value";
      const geographyValues = currentConfig.geographyValues || {};

      // Calculate class breaks and colors (same as layer creation)
      const values = Object.values(geographyValues).map(v => parseFloat(v)).filter(v => !isNaN(v));
      const sortedValues = [...values].sort((a, b) => a - b);
      const numBreaks = 5;
      const classBreaks = [];

      for (let i = 0; i < numBreaks; i++) {
        const quantileIndex = Math.floor((sortedValues.length - 1) * (i + 1) / numBreaks);
        classBreaks.push(sortedValues[quantileIndex]);
      }

      // TCG color palette (matching layer creation)
      const tcgColors = [
        { rgb: [191, 0, 0, 0.6], hex: '#BF0000', name: 'Red' },
        { rgb: [255, 122, 13, 0.5], hex: '#FF7A0D', name: 'Orange' },
        { rgb: [248, 242, 0, 0.5], hex: '#F8F200', name: 'Yellow' },
        { rgb: [0, 191, 44, 0.5], hex: '#00BF2C', name: 'Green' },
        { rgb: [0, 51, 128, 0.6], hex: '#003380', name: 'Blue' },
      ];

      const classBreakInfos = classBreaks.map((breakValue, index) => ({
        minValue: index === 0 ? -Infinity : classBreaks[index - 1],
        maxValue: breakValue,
        color: tcgColors[index],
        label: index === 0 ?
          `< ${breakValue.toFixed(2)}` :
          `${classBreaks[index - 1].toFixed(2)} - ${breakValue.toFixed(2)}`
      }));

      // Function to get break info for a value
      const getBreakForValue = (value) => {
        for (let i = 0; i < classBreakInfos.length; i++) {
          const breakInfo = classBreakInfos[i];
          if (value > breakInfo.minValue && value <= breakInfo.maxValue) {
            return breakInfo;
          }
        }
        return classBreakInfos[classBreakInfos.length - 1];
      };

      return (
        <div className="p-4">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {isValueBased ? 'Value-Based Geography Map' : 'Geography Heat Map Configuration'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                This map visualizes {currentConfig.areaType || 'geographic areas'} {isValueBased ? 'with your uploaded values' : 'with demographic data'}.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {isValueBased ? 'Field Name' : 'Variable'}
                </label>
                <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                  {currentConfig.field || 'Value'}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Geography Type
                </label>
                <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                  {currentConfig.areaType || 'ZIP Code'}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Areas
                </label>
                <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  {currentConfig.geographyList?.join(', ') || 'All areas'}
                </div>
              </div>
            </div>

            {isValueBased && Object.keys(geographyValues).length > 0 && (
              <>
                {/* Class Breaks Legend */}
                <div className="mt-4">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
                    Class Breaks
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-3 space-y-2">
                    {classBreakInfos.map((breakInfo, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-500 flex-shrink-0"
                          style={{ backgroundColor: breakInfo.color.hex }}
                        />
                        <div className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                          {breakInfo.label}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {breakInfo.color.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Area Values Table */}
                <div className="mt-4">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
                    Area Values
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-gray-600 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Area</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Value</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Break</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                          {Object.entries(geographyValues).map(([area, value]) => {
                            const breakInfo = getBreakForValue(parseFloat(value));
                            return (
                              <tr key={area} className="hover:bg-gray-100 dark:hover:bg-gray-600">
                                <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium">{area}</td>
                                <td className="px-3 py-2">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={value}
                                    onChange={(e) => {
                                      const newValues = { ...geographyValues, [area]: parseFloat(e.target.value) };
                                      handleConfigChange({ ...currentConfig, geographyValues: newValues });
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-4 h-4 rounded border border-gray-300 dark:border-gray-500 flex-shrink-0"
                                      style={{ backgroundColor: breakInfo.color.hex }}
                                    />
                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                      {breakInfo.color.name}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs text-blue-800 dark:text-blue-200">
                <strong>Note:</strong> {isValueBased ? 'Edit values above and click Preview to see changes.' : 'To change the variable or geography selection, create a new geography-based map.'}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4 text-gray-500 dark:text-gray-400">
        No specific editor for: effectiveType '{effectiveType || "unknown"}' / rendererType '{rendererType || "unknown"}'.
        <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-auto max-h-60">
          Current Config: {JSON.stringify(currentConfig, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div
      className={`
      fixed inset-y-0 right-0 z-30 w-[500px] h-full flex flex-col bg-white dark:bg-gray-800 shadow-xl
      transform transition-transform duration-300 ease-in-out border-l border-gray-200 dark:border-gray-700
      ${isOpen ? "translate-x-0" : "translate-x-full"}
    `}
    >
      <div className="flex-none h-16 flex items-center justify-between px-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            {isLabelEditMode ? "Edit Labels" : `Edit Values (${effectiveType})`}
          </h2>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6">{renderEditor()}</div>
      <div className="flex-none p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center justify-between">
          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && !isLabelEditMode && (
            <div className="flex items-center text-sm text-amber-600 dark:text-amber-400">
              <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Unsaved changes
            </div>
          )}
          {!hasUnsavedChanges && <div />}
          <div className="flex space-x-3">
            <button onClick={handleCancel} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">Cancel</button>
            {!isLabelEditMode && (
              <button
                onClick={handleSave}
                disabled={!currentConfig || !effectiveType || isTransitioning || isSaving}
                className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              ><Save size={16} className={isSaving ? "animate-spin" : ""} /><span>{isSaving ? "Saving..." : "Apply & Close"}</span></button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayerPropertiesEditor;